import {useEffect, useState} from 'react';

import {
  readStoredPeerUniverse,
  syncPeerUniverseForCurrentCompany,
  type PeerUniverse
} from '../services/modules/ClimateDB/peerUniverseAPI';

interface UsePeerUniverseResult {
  universe: PeerUniverse | null;
  selectedPeerIds: number[];
  setSelectedPeerIds: (peerIds: number[]) => void;
  isLoading: boolean;
}

/**
 * Reads the peer universe saved for this company and year. The network call
 * happens after /get-token, and again only when the company id or year changes.
 */
export const usePeerUniverse = (
  companyId: string,
  selectedYear: string
): UsePeerUniverseResult => {
  const cachedUniverse = readStoredPeerUniverse(companyId, selectedYear);
  const [universe, setUniverse] = useState<PeerUniverse | null>(cachedUniverse);
  const [selectedPeerIds, setSelectedPeerIds] = useState<number[]>(
    cachedUniverse?.defaultPeerIds ?? []
  );
  const [isLoading, setIsLoading] = useState(
    () =>
      Boolean(companyId) && !readStoredPeerUniverse(companyId, selectedYear)
  );
  const [trackedCompanyId, setTrackedCompanyId] = useState(companyId);
  const [trackedYear, setTrackedYear] = useState(selectedYear);

  if (companyId !== trackedCompanyId || selectedYear !== trackedYear) {
    const nextCached = readStoredPeerUniverse(companyId, selectedYear);
    setTrackedCompanyId(companyId);
    setTrackedYear(selectedYear);
    setUniverse(nextCached);
    setSelectedPeerIds(nextCached?.defaultPeerIds ?? []);
    setIsLoading(!nextCached && Boolean(companyId));
  }

  useEffect(() => {
    if (!companyId || readStoredPeerUniverse(companyId, selectedYear)) {
      return;
    }

    let isCurrent = true;

    setIsLoading(true);
    syncPeerUniverseForCurrentCompany(false, selectedYear)
      .then((next) => {
        if (!isCurrent) {
          return;
        }

        setUniverse(next);
        setSelectedPeerIds(next?.defaultPeerIds ?? []);
      })
      .catch(() => {
        if (isCurrent) {
          setUniverse(null);
          setSelectedPeerIds([]);
        }
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [companyId, selectedYear]);

  return {universe, selectedPeerIds, setSelectedPeerIds, isLoading};
};
